import { useEffect, useState, useRef, useCallback } from 'react';
import type { RefObject } from 'react';
import { SCREEN_DESIGN_SIZE } from '../constants/designSize';

export interface UseScreenScaleOptions {
  padding?: number;
  minScale?: number;
  maxScale?: number;
  debounce?: number;
  contentWidth?: number;
  contentHeight?: number;
}

export interface UseScreenScaleResult {
  scale: number;
  containerWidth: number;
  containerHeight: number;
  isReady: boolean;
  scaledWidth: number;
  scaledHeight: number;
}

export function useScreenScale(
  containerRef: RefObject<HTMLElement | null>,
  options: UseScreenScaleOptions = {}
): UseScreenScaleResult {
  const {
    padding = 0, minScale = 0.1, maxScale = 1.0,
    debounce = 16, contentWidth, contentHeight,
  } = options;

  const targetWidth = contentWidth ?? SCREEN_DESIGN_SIZE.WIDTH;
  const targetHeight = contentHeight ?? SCREEN_DESIGN_SIZE.HEIGHT;

  const [result, setResult] = useState<UseScreenScaleResult>({
    scale: 1, containerWidth: 0, containerHeight: 0,
    isReady: false,
    scaledWidth: SCREEN_DESIGN_SIZE.WIDTH,
    scaledHeight: SCREEN_DESIGN_SIZE.HEIGHT,
  });

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const calculateScale = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const containerWidth = container.clientWidth - padding * 2;
    const containerHeight = container.clientHeight - padding * 2;
    if (containerWidth <= 0 || containerHeight <= 0) return;
    const scaleX = containerWidth / targetWidth;
    const scaleY = containerHeight / targetHeight;
    const rawScale = Math.min(scaleX, scaleY);
    const scale = Math.max(minScale, Math.min(maxScale, rawScale));
    setResult({ scale, containerWidth, containerHeight, isReady: true,
      scaledWidth: targetWidth * scale, scaledHeight: targetHeight * scale });
  }, [containerRef, padding, minScale, maxScale, targetWidth, targetHeight]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleResize = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(calculateScale, debounce);
    };
    calculateScale();
    const observer = new ResizeObserver(handleResize);
    observer.observe(container);
    return () => {
      observer.disconnect();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [containerRef, calculateScale, debounce]);

  return result;
}
